/**
 * `/locations/mexico/<locationSlug>/`: one Mexican location's page, rendered by
 * `components/Locations/IndividualLocationContainer.tsx`. Ryerson build only.
 *
 * How Mexico differs from the US and Canada pages:
 * - There is no state segment. The paths are every brand location whose Contentful `country` is
 *   exactly `"Mexico"` (`src/contentful/location/getContentfulLocationSlugsByCountry.ts`); the
 *   header location data isn't read here, and the location's `state` doesn't matter.
 * - There is no map page: `/locations/mexico/` isn't built. Mexican locations appear on the other
 *   map pages, which list every brand location.
 * `components/Locations/IndividualLocationContainer.tsx` (canonical) and
 * `components/Locations/LocationsOnMap.tsx` (pin links) drop the state segment for
 * `country === "Mexico"` to match.
 *
 * The details are looked up by slug alone, as on the other location pages. The route tree is in
 * `src/pages/locations/index.tsx`. Pinned by
 * `src/unit-test/pages/locations/mexico/locationSlug.spec.tsx`.
 */
import React from "react";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import { ProcessedContentfulLocationAllFieldsByLanguage } from "@contentful/location/types";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import { GetStaticProps } from "next";
import { getContentfulLocationSlugsByCountry } from "@contentful/location/getContentfulLocationSlugsByCountry";
import IndividualLocationContainer from "@components/Locations/IndividualLocationContainer";
import { getContentfulLocationDetailsByLocationSlug } from "@contentful/location/getContentfulLocationDetailsByLocationSlug";

/**
 * One path per Mexican location slug; no paths, and no Contentful call, on any brand but
 * Ryerson.
 */
export const getStaticPaths = async () => {
	// skip page generation
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		// If company is not ryerson, in local dev (yarn start) next.js will still try to serve the page for any URL but since getStaticProps will not run, the page will break.
		// In production (static export), unknown paths return 404.html — so this is a local dev-only quirk.
		return { paths: [], fallback: false };
	}

	const locationSlugs = await getContentfulLocationSlugsByCountry("Mexico");

	return {
		paths: locationSlugs.map((locationSlug) => ({
			params: {
				locationSlug,
			},
		})),
		fallback: false, // returns 404 for all other paths
	};
};

type PageParams = {
	locationSlug: string;
};

type PageProps = {
	contentfulLocationByLanguage: ProcessedContentfulLocationAllFieldsByLanguage;
};

/**
 * Build time: the header data and the location in all three languages. The lookup throws when the
 * slug has no entry in one of the languages, which fails the build.
 */
export const getStaticProps: GetStaticProps<PageProps, PageParams> = async ({ params }) => {
	const { props: headerFooterProps } = await allContentfulHeaderCalls();

	return {
		props: {
			...headerFooterProps,
			contentfulLocationByLanguage: await getContentfulLocationDetailsByLocationSlug(
				params?.locationSlug as string
			), // type narrowing because params will be there
		},
	};
};

/** A Mexican location's page; see the file header. */
const Page: React.FC<HeaderPageProps & PageProps> = (props) => {
	return (
		<HeaderLayout {...getHeaderData(props)}>
			<IndividualLocationContainer
				contentfulLocationByLanguage={props.contentfulLocationByLanguage}
			/>
		</HeaderLayout>
	);
};

export default Page;
